import { observer } from 'mobx-react-lite'
import { useLoaderData } from 'react-router'
import { useFeatures } from '@/contexts/root-store-context'
import { type NotificationsPageViewModel } from '@/features/notifications/NotificationsPageViewModel'
import { type NotificationListViewModel } from '@/features/notifications/NotificationListViewModel'
import { type NotificationThreadViewModel } from '@/features/notifications/NotificationThreadViewModel'
import { JobPaneContainer } from '@/features/jobs/details/components/JobRoot/JobPaneContainer'
import type { JobPaneLoaderData } from '@/features/jobs/job-route-loaders'
import { WorkspaceSelectorViewHeadingContainer } from '@/features/iam/workspaces/components/WorkspaceSelectorViewHeading/WorkspaceSelectorViewHeadingContainer'
import { NotificationListEmpty } from '@/features/notifications/components/NotificationList'
import { ErrorBlock } from '@/components/ErrorBlock'
import { InfiniteScrollLoaderContainer } from '@/components/InfiniteScrollLoader/InfiniteScrollLoaderContainer'
import { useActivation } from '@/hooks/view-model-hooks'
import { useIsSidebarCollapsed } from '@/hooks/viewport-hooks'
import {
  NoJobSelected,
  NotificationsListHeader,
  NotificationsPage,
  ThreadItem,
  ThreadList,
} from '@/features/notifications/components/NotificationsPage'

export default observer(function NotificationsPageContainer() {
  const { notifications } = useFeatures((r) => ({
    notifications: r.notifications.internal(),
  }))
  const vm = notifications.notificationsPageVm
  const data = useLoaderData<JobPaneLoaderData>()
  const wide = !useIsSidebarCollapsed()
  useActivation(vm)

  const listVisible = vm.isListVisible(wide)

  return (
    <NotificationsPage
      listVisible={listVisible}
      detailVisible={vm.isDetailVisible(wide)}
      listHeader={<ListHeaderContainer vm={vm} />}
      threadList={
        <ThreadListContainer vm={vm.list} selectedJobId={vm.selectedJobId} />
      }
      detail={
        <JobPaneContainer
          data={data}
          empty={
            <NoJobSelected
              text={vm.unreadCountText}
              isSkeleton={vm.list.isFetchingInitial}
            />
          }
          isJobListSidebarVisible={listVisible}
          onJobListSidebarToggle={wide ? vm.toggleListVisibility : undefined}
          onPreviousJob={vm.previousJobId ? vm.goToPreviousJob : undefined}
          onNextJob={vm.nextJobId ? vm.goToNextJob : undefined}
        />
      }
    />
  )
})

interface ListHeaderContainerProps {
  vm: NotificationsPageViewModel
}

const ListHeaderContainer = observer(function ListHeaderContainer({
  vm,
}: ListHeaderContainerProps) {
  return (
    <NotificationsListHeader
      workspaceSelector={
        <WorkspaceSelectorViewHeadingContainer
          value={vm.selectedWorkspace}
          onChange={vm.selectWorkspace}
          primaryText="Notifications"
          allOptionText="All workspaces"
          workspaces={vm.workspaces}
          dropdownTitle="View notifications for"
        />
      }
      isMarkAllAsReadDisabled={vm.isMarkAllAsReadDisabled}
      onMarkAllAsRead={vm.list.markAllAsRead}
    />
  )
})

interface ThreadListContainerProps {
  vm: NotificationListViewModel
  selectedJobId: string | null
}

const ThreadListContainer = observer(function ThreadListContainer({
  vm,
  selectedJobId,
}: ThreadListContainerProps) {
  return (
    <ThreadList
      loader={
        vm.isRevalidating ? null : (
          <InfiniteScrollLoaderContainer scrollProvider={vm.scroller} />
        )
      }
    >
      {vm.fetch.match({
        pending: () =>
          vm.isRevalidating ? (
            <ThreadItemsContainer vm={vm} selectedJobId={selectedJobId} />
          ) : null,
        rejected: (err) => <ErrorBlock tackOn="top" error={err} />,
        resolved: () => (
          <ThreadItemsContainer vm={vm} selectedJobId={selectedJobId} />
        ),
      })}
    </ThreadList>
  )
})

const ThreadItemsContainer = observer(function ThreadItemsContainer({
  vm,
  selectedJobId,
}: ThreadListContainerProps) {
  const { shellInfo } = useFeatures(({ shell }) => ({
    shellInfo: shell.shellInfo,
  }))

  if (vm.items.length === 0) {
    return <NotificationListEmpty corporateMode={shellInfo.corporateMode} />
  }

  return vm.threads.map((thread) => (
    <ThreadItemContainer
      key={thread.key}
      threadVm={thread}
      selected={Boolean(selectedJobId) && thread.jobId === selectedJobId}
    />
  ))
})

interface ThreadItemContainerProps {
  threadVm: NotificationThreadViewModel
  selected: boolean
}

const ThreadItemContainer = observer(function ThreadItemContainer({
  threadVm,
  selected,
}: ThreadItemContainerProps) {
  return (
    <ThreadItem
      selected={selected}
      onOpen={threadVm.markAsRead}
      to={threadVm.href}
      unread={threadVm.isUnread}
      imageSource={threadVm.latest.imageSource}
      jobDisplayName={threadVm.jobDisplayName}
      text={threadVm.latest.text}
      plainText={threadVm.latest.plainText}
      dispatchedAt={threadVm.latest.dispatchedAt}
      context={threadVm.context}
      earlier={threadVm.earlier}
    />
  )
})
